import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import {
    getReviewSelectionSessionStorageKey,
    readStoredPickedBrandGroups,
    reconcilePickedIds,
    resolveReviewSelectionSessionId,
    writeStoredPickedBrandGroups,
} from '@/context/reviews-page-transport.helpers';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { useEffect, useRef } from 'react';

const reviewSelectionLogger = createClientLogger({
    scope: 'review-selection',
});

type UseSelectionSessionBootstrapArgs = {
    clientId: string;
    setSelectionSessionId: (value: string) => void;
};

export const useSelectionSessionBootstrap = ({ clientId, setSelectionSessionId }: UseSelectionSessionBootstrapArgs) => {
    useEffect(() => {
        const storageKey = getReviewSelectionSessionStorageKey(clientId);
        const existingSessionId = window.sessionStorage.getItem(storageKey);
        const generatedSessionId = resolveReviewSelectionSessionId(existingSessionId, {
            randomUuid: globalThis.crypto?.randomUUID?.bind(globalThis.crypto),
        });

        if (!existingSessionId) {
            window.sessionStorage.setItem(storageKey, generatedSessionId);
        }

        setSelectionSessionId(generatedSessionId);
    }, [clientId, setSelectionSessionId]);
};

type UseProductSelectionSessionSyncArgs = {
    accessToken: string;
    selectionSessionId: string;
    pickedProducts: string[];
    setPickedProducts: (value: string[]) => void;
};

export const useProductSelectionSessionSync = ({
    accessToken,
    selectionSessionId,
    pickedProducts,
    setPickedProducts,
}: UseProductSelectionSessionSyncArgs) => {
    const { productsApi } = useMarketplaceApi();
    const latestLoadRequestIdRef = useRef(0);
    const latestPickedProductsRef = useRef(pickedProducts);

    useEffect(() => {
        latestPickedProductsRef.current = pickedProducts;
    }, [pickedProducts]);

    useEffect(() => {
        if (!selectionSessionId) {
            return;
        }

        const abortController = new AbortController();
        const requestId = latestLoadRequestIdRef.current + 1;
        const pickedProductsAtRequestStart = latestPickedProductsRef.current;

        latestLoadRequestIdRef.current = requestId;

        void productsApi
            .getProductSelectionSession(selectionSessionId, {
                signal: abortController.signal,
                authToken: accessToken,
            })
            .then((response) => {
                if (abortController.signal.aborted || latestLoadRequestIdRef.current !== requestId) {
                    return;
                }

                // Ignore stale bootstrap responses once the operator has already changed product selection locally.
                if (latestPickedProductsRef.current !== pickedProductsAtRequestStart) {
                    return;
                }

                setPickedProducts(response.data.ids);
            })
            .catch((error) => {
                if (isRequestCanceled(error)) {
                    return;
                }

                logClientError({
                    logger: reviewSelectionLogger,
                    message: '[client.review-selection.product-session-load] Failed to load product selection session.',
                    error,
                    context: {
                        selectionSessionId,
                    },
                });
            });

        return () => {
            abortController.abort();
        };
    }, [accessToken, productsApi, selectionSessionId, setPickedProducts]);
};

type UsePickedProductsReconcileArgs = {
    accessToken: string;
    selectionSessionId: string;
    pickedProducts: string[];
    products: ProductGroupEntity[];
    setPickedProducts: (value: string[]) => void;
};

export const usePickedProductsReconcile = ({
    accessToken,
    selectionSessionId,
    pickedProducts,
    products,
    setPickedProducts,
}: UsePickedProductsReconcileArgs) => {
    const { productsApi } = useMarketplaceApi();

    useEffect(() => {
        if (!pickedProducts.length) {
            return;
        }

        const availableProductIds = new Set(products.map((product) => product.id));
        const nextPickedProducts = reconcilePickedIds(pickedProducts, availableProductIds);

        if (nextPickedProducts === pickedProducts) {
            return;
        }

        if (selectionSessionId) {
            void productsApi
                .setProductSelectionSession(selectionSessionId, nextPickedProducts, { authToken: accessToken })
                .catch((error) =>
                    logClientError({
                        logger: reviewSelectionLogger,
                        message:
                            '[client.review-selection.product-session-persist] Failed to persist reconciled product selection session.',
                        error,
                        context: {
                            selectionSessionId,
                            pickedProducts: nextPickedProducts.length,
                        },
                    }),
                );
        }

        setPickedProducts(nextPickedProducts);
    }, [accessToken, pickedProducts, products, productsApi, selectionSessionId, setPickedProducts]);
};

type UsePickedBrandGroupsStorageSyncArgs = {
    clientId: string;
    pickedBrandGroups: string[];
    setPickedBrandGroups: (value: string[]) => void;
};

export const usePickedBrandGroupsStorageSync = ({
    clientId,
    pickedBrandGroups,
    setPickedBrandGroups,
}: UsePickedBrandGroupsStorageSyncArgs) => {
    const skipInitialPersistRef = useRef(true);

    useEffect(() => {
        skipInitialPersistRef.current = true;
        setPickedBrandGroups(readStoredPickedBrandGroups(clientId));
    }, [clientId, setPickedBrandGroups]);

    useEffect(() => {
        if (skipInitialPersistRef.current) {
            skipInitialPersistRef.current = false;
            return;
        }

        writeStoredPickedBrandGroups(clientId, pickedBrandGroups);
    }, [clientId, pickedBrandGroups]);
};

type UsePickedBrandGroupsReconcileArgs = {
    brandGroups: BrandGroupEntity[];
    pickedBrandGroups: string[];
    setPickedBrandGroups: (value: string[]) => void;
};

export const usePickedBrandGroupsReconcile = ({
    brandGroups,
    pickedBrandGroups,
    setPickedBrandGroups,
}: UsePickedBrandGroupsReconcileArgs) => {
    useEffect(() => {
        if (!pickedBrandGroups.length) {
            return;
        }

        const availableBrandGroupIds = new Set(brandGroups.map((brandGroup) => brandGroup.id));
        const nextPickedBrandGroups = reconcilePickedIds(pickedBrandGroups, availableBrandGroupIds);

        if (nextPickedBrandGroups === pickedBrandGroups) {
            return;
        }

        setPickedBrandGroups(nextPickedBrandGroups);
    }, [brandGroups, pickedBrandGroups, setPickedBrandGroups]);
};
